import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Signal,
    signal,
    viewChild,
    WritableSignal
} from "@angular/core";
import {navParam} from "../../navigation/navigation-params";
import {Page} from "../../navigation/page";
import {WalletProvider} from '../../payment/payment';
import {PaymentModel} from '../../payment/payment.model';
import {PhoneNumber} from '../../utils/phone-number';
import {FormComponent} from '../../inputs/form/form.component';
import {PhoneNumberFieldComponent} from '../../inputs/phone-number-field/phone-number-field.component';
import {ContentMessageComponent} from "../../notification/message/content-message.component";
import {MessageLevel} from "../../notification/message/message";
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {FormsModule} from '@angular/forms';
import {SmsPhoneNumberDirective} from '../../inputs/phone-number-field/sms-phone-number.directive';
import {ButtonComponent} from '../../buttons/button/button.component';
import {PageFooterControlDirective} from '../../page-layout/page-footer-control.directive';
import {ConfigurationModel} from '../../configuration/configuration.model';

@Component({
    selector: "woa-phone-edit-page",
    templateUrl: "./phone-edit-page.component.html",
    providers: [{provide: Page, useExisting: PhoneEditPageComponent}],
    imports: [
        ButtonComponent,
        ContentMessageComponent,
        FormComponent,
        FormsModule,
        ModalPageLayoutComponent,
        PageFooterControlDirective,
        PhoneNumberFieldComponent,
        SmsPhoneNumberDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhoneEditPageComponent extends Page {
    private paymentModel: PaymentModel = inject(PaymentModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);

    MessageLevel = MessageLevel;

    public readonly form: Signal<FormComponent> = viewChild.required(FormComponent);

    private readonly mobilePhone: Signal<string> = navParam.required<string>("mobilePhone");

    public readonly suppliedMobilePhone: WritableSignal<string> = signal<string>(this.mobilePhone());

    public readonly confirmedMobilePhone: WritableSignal<string> = signal((() => {
        if (this.mobilePhone()?.startsWith("+")) {
            const phoneNumber: PhoneNumber | null = PhoneNumber.parse(this.mobilePhone());
            if (phoneNumber) {
                const localPhoneNumber: string = "+" + this.configurationModel.app()?.expectedCountryCode + phoneNumber.nationalNumber;
                if (this.configurationModel.isValidMobileNumberForSms()(localPhoneNumber)) {
                    return localPhoneNumber;
                }
            }
        } else if (this.configurationModel.isValidMobileNumberForSms()(this.mobilePhone())) {
            return this.mobilePhone();
        }
        return undefined;
    })());

    public readonly error: Signal<string> = computed<string>(() => {
        const walletProvider: WalletProvider = this.paymentModel.walletPaymentProcessor()?.walletProvider;
        return !this.suppliedMobilePhone() ? (walletProvider === WalletProvider.APPLE_PAY ? "woa.message.phone.missing.applyPay"
                : walletProvider === WalletProvider.GOOGLE_PAY ? "woa.message.phone.missing.googlePay"
                    : "woa.message.phone.missing.non-integrated")
            : this.configurationModel.isValidMobileNumberForSms()(this.suppliedMobilePhone()) ? (walletProvider === WalletProvider.APPLE_PAY ? "woa.message.phone.invalid.applyPay"
                    : walletProvider === WalletProvider.GOOGLE_PAY ? "woa.message.phone.invalid.googlePay"
                        : "woa.message.phone.invalid.non-integrated")
                : (walletProvider === WalletProvider.APPLE_PAY ? "woa.message.phone.non-mobile.applyPay"
                    : walletProvider === WalletProvider.GOOGLE_PAY ? "woa.message.phone.non-mobile.googlePay"
                        : "woa.message.phone.non-mobile.non-integrated");
    });

    public readonly instructions: Signal<string> = computed<string>(() =>
        !this.suppliedMobilePhone() ? "woa.message.phone.instructions.missing"
            : this.configurationModel.isValidMobileNumberForSms()(this.suppliedMobilePhone()) ? "woa.message.phone.instructions.invalid"
                : "woa.message.phone.instructions.non-mobile");

    confirmPhone(): void {
        if (!this.form().valid) {
            return;
        }

        void this.dismiss({
            confirmed: true,
            mobilePhone: this.confirmedMobilePhone()
        });
    }
}
